import React, { useState } from 'react';
import {arrayOf, shape, func, number} from "prop-types";
import { getFormattedDateDisplay, getFormattedTimeSlot } from '../../../tools/date.util';
import moment from 'moment';
import GmproGroupingCard from './GmproGroupingCard';

// The body of the GMPRO "Proposed" tab: renders each pending recommendation as
// a card, grouped under a "{timeslot} - {date} [count]" header — the same
// timeslot/date grouping the Bookings/Unassigned/Assigned tabs use. Only one
// card can be in the Reject state at a time (tracked here), so opening Reject on
// another card closes the previous one. The Approve, Reject (driver/grouping)
// and Locate actions are passed up to EnhancedGroupedTrips, which performs the
// optimistic chain removal + re-fetch.
const GmproGroupings = ({
  groupings,
  onApprove,
  onRejectDriver,
  onRejectGrouping,
  onLocate,
  activeRouteGroupingId,
}) => {
  const [rejectingId, setRejectingId] = useState(null);

  // Group the proposals by their timeslot + date, mirroring the other tabs.
  const grouped = groupings.reduce((acc, grouping) => {
    const key = `${grouping.timeslot}-${getFormattedDateDisplay(grouping.date)}`;
    const bucket = acc[key] || [];
    bucket.push(grouping);
    acc[key] = bucket;
    return acc;
  }, {});

  // Sort the headers by date, then timeslot, ascending (same comparison the
  // other tabs use).
  const sortedKeys = Object.keys(grouped).sort((a, b) => {
    const [timeslotA, dateA] = a.split('-');
    const [timeslotB, dateB] = b.split('-');
    const compare = (x, y) => (x > y) - (x < y);
    return (
      compare(moment(dateA, 'DD/MM/YYYY'), moment(dateB, 'DD/MM/YYYY')) ||
      compare(
        moment(getFormattedTimeSlot(timeslotA), 'HH:mmA'),
        moment(getFormattedTimeSlot(timeslotB), 'HH:mmA')
      )
    );
  });

  // Letter each proposal A, B, C… in display order — this letter prefixes the
  // card's id (e.g. A-000001).
  const letterFor = {};
  let letterIndex = 0;
  sortedKeys.forEach((key) => {
    grouped[key].forEach((grouping) => {
      letterFor[grouping.id] = String.fromCodePoint(65 + (letterIndex % 26));
      letterIndex += 1;
    });
  });

  return (
    <div
      data-testid={groupings.length === 0 ? 'gmpro-groupings-empty' : 'gmpro-groupings-list'}
      className="h-full mt-4 pr-2 pb-8 overflow-y-auto"
      style={{ maxHeight: '400px' }}>
      {sortedKeys.map((key) => {
        const proposals = grouped[key];
        const [timeslot, date] = key.split('-');
        return (
          <div key={key} className="w-full my-2">
            <h2
              className="text-lg text-gray-700 ml-2 font-bold"
              style={{ marginBottom: '16px' }}>
              {timeslot} - {date} [{proposals.length}]
            </h2>
            {proposals.map((grouping) => (
              <GmproGroupingCard
                key={grouping.id}
                grouping={grouping}
                letter={letterFor[grouping.id]}
                rejecting={rejectingId === grouping.id}
                routeActive={activeRouteGroupingId === grouping.id}
                onStartReject={() => setRejectingId(grouping.id)}
                onCloseReject={() => setRejectingId(null)}
                onApprove={onApprove}
                onRejectDriver={onRejectDriver}
                onRejectGrouping={onRejectGrouping}
                onLocate={onLocate}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

GmproGroupings.propTypes = {
  groupings: arrayOf(shape({})),
  onApprove: func,
  onRejectDriver: func,
  onRejectGrouping: func,
  onLocate: func,
  activeRouteGroupingId: number,
};

export default GmproGroupings;
